// Toplu Studio — Liste Denetimi mantigiyla fotograf uretimi.
//
// Secilen her urun ayri bir kart: sahneleri (konseptler + sahneye ozel
// talimat), modeli, manken ayari, ek talimati, referans fotografi ve
// kisisellestirme ornekleri KARTA OZELDIR. "Tumunu uretime baslat" her kart
// icin sunucuda ayri bir is acar; kuyruk tarayici kapansa da surer.
//
// Varsayilanlar Studio ile ortak: urun turunun son ayarlari (UserSetting
// "jewelry_shot_prefs") ve son kullanilan sahne seti. Kartlardaki degisiklikler
// tarayicida oturum olarak saklanir.

import { useEffect, useMemo, useRef, useState } from "react";
import { queueForReview, AUTO_AI_KEY } from "./JewelryReviewPage";
import { Link, useNavigate } from "react-router-dom";
import {
  CheckCircle2, ClipboardCheck, Loader2, Play, Plus, RotateCcw, Search, Sparkles, Square, Trash2, X, XCircle,
} from "lucide-react";
import { apiClient } from "../lib/api";
import { getBackendUrl, authHeaders } from "../lib/http";
import { useShopVertical } from "../lib/useShopVertical";
import { useJewelryTypes } from "../lib/useJewelryTypes";
import { productJewelryType } from "../lib/jewelryType";
import { shotProductKey, useShotProducts } from "../lib/useShotProducts";
import { ImageLightbox } from "../components/ImageLightbox";
import { InspirationConceptPicker } from "../components/InspirationConceptPicker";
import type { CustomMockup, ProductCategory, ProductPersonalization, SupplierCatalog } from "@erc/shared";

const abs = (u?: string | null) => (!u ? "" : u.startsWith("http") || u.startsWith("data:") ? u : `${getBackendUrl()}${u}`);
const SESSION_KEY = "erc:jewelry-bulk-studio:session";
const AUTO_REVIEW_KEY = "erc_bulk_auto_review";

type LookMode = "keep" | "auto" | "custom";
type Look = { mode: LookMode; skinTone?: string; hairColor?: string; hairTexture?: string; age?: string; nails?: string; makeup?: string };
/** Studio ve sunucudaki MODEL_LOOK_OPTIONS ile ayni degerler */
const LOOK_OPTIONS = {
  skinTone: ["Ten", [["fair porcelain", "Çok açık"], ["light beige", "Açık bej"], ["warm olive", "Zeytin"], ["medium tan", "Buğday"], ["golden brown", "Altın esmer"], ["deep brown", "Esmer"], ["rich dark ebony", "Koyu esmer"]]],
  hairColor: ["Saç rengi", [["platinum blonde", "Platin sarı"], ["honey blonde", "Bal sarısı"], ["light brown", "Açık kahve"], ["chestnut brown", "Kestane"], ["dark brown", "Koyu kahve"], ["jet black", "Siyah"], ["auburn red", "Kızıl"]]],
  hairTexture: ["Saç", [["sleek straight", "Düz"], ["soft waves", "Dalgalı"], ["loose curls", "Bukleli"], ["tight coils", "Kıvırcık"], ["braided", "Örgülü"]]],
  age: ["Yaş", [["early 20s", "20'lerin başı"], ["late 20s", "20'lerin sonu"], ["30s", "30'lar"], ["40s", "40'lar"], ["50s", "50'ler"]]],
  nails: ["Oje", [["bare natural nails", "Ojesiz"], ["nude polish", "Nude"], ["soft pink polish", "Açık pembe"], ["classic red polish", "Kırmızı"], ["french manicure", "French"], ["deep burgundy polish", "Bordo"]]],
  makeup: ["Makyaj", [["no-makeup natural look", "Makyajsız"], ["soft natural makeup", "Doğal"], ["glowy dewy makeup", "Işıltılı"], ["defined elegant makeup", "Belirgin"]]],
} as const;
type LookKey = keyof typeof LOOK_OPTIONS;

/** Karta ozel uretim ayarlari */
interface CardSettings {
  conceptIds: string[];
  /** Sahneye ozel ek talimat */
  conceptPrompts: Record<string, string>;
  /** Sahneye ozel urun fotografi (conceptId -> url); yoksa urun referansi */
  conceptRefs?: Record<string, string>;
  model: string;
  look: Look;
  extraPrompt: string;
  persoValues: string[];
  /** Secilen urun referansi (yoksa katalogdaki) */
  refUrl?: string;
  /** Son uygulanan sahne seti */
  setId?: string;
}
type JobItem = { id: string; conceptId: string | null; status: string; error?: string | null; imageUrl?: string | null };
type Job = { id: string; status: string; total: number; doneCount: number; failedCount: number; items?: JobItem[] };
interface Session { selected: string[]; cards: Record<string, CardSettings>; jobs: Record<string, string> }
type ShotSettings = { look?: Look; model?: string; extraPrompt?: string };
/** Studio ile ortak (UserSetting "jewelry_shot_prefs"): presets = Studio'daki "Kayıtlı ayar"lar */
type ShotPrefs = { byType: Record<string, ShotSettings>; presets?: Array<{ id: string; name: string; s: ShotSettings }> };
type SavedLook = { id: string; name: string; look: Look };
const sameLook = (a: Look, b: Look) => JSON.stringify(a) === JSON.stringify(b);
type Row = { key: string; catalog: SupplierCatalog; cat: ProductCategory };

function loadSession(): Session {
  try {
    const s = JSON.parse(localStorage.getItem(SESSION_KEY) || "") as Partial<Session>;
    return { selected: s.selected ?? [], cards: s.cards ?? {}, jobs: s.jobs ?? {} };
  } catch { return { selected: [], cards: {}, jobs: {} }; }
}

/** Studio'daki personalizationClause ile ayni metin */
function personalizationClause(p: ProductPersonalization | undefined, values: string[]): string {
  if (!p?.enabled) return "";
  const v = values.map((x) => x.trim()).filter(Boolean);
  if (v.length === 0) return "";
  const font = p.font?.trim();
  return [
    `The jewellery is personalised and must clearly show ${v.length > 1 ? "the names" : "the name"} ${v.map((x) => `"${x}"`).join(" and ")}.`,
    "Spell them EXACTLY as written, correct letter by letter, no extra or missing characters.",
    font ? `Lettering style: ${font}.` : "Lettering must match the style of the product photo.",
    "Keep the text sharp, legible and correctly proportioned on the piece.",
    p.note?.trim() ? `Note: ${p.note.trim()}` : "",
  ].filter(Boolean).join(" ");
}

export default function JewelryBulkStudioPage() {
  const { vertical } = useShopVertical();
  const types = useJewelryTypes();
  const typeLabel = (k: string | null) => (k ? types.find((t) => t.key === k)?.label ?? k : "Diğer");
  const navigate = useNavigate();

  const [catalogs, setCatalogs] = useState<SupplierCatalog[]>([]);
  const [concepts, setConcepts] = useState<CustomMockup[]>([]);
  const [sets, setSets] = useState<Array<{ id: string; name: string; mockups: Array<{ id: string }> }>>([]);
  const [models, setModels] = useState<Array<{ id: string; name: string; price?: string }>>([]);
  const [prefs, setPrefs] = useState<ShotPrefs>({ byType: {} });
  const [loading, setLoading] = useState(true);
  const [session, setSession] = useState<Session>(loadSession);
  const [jobs, setJobs] = useState<Record<string, Job>>({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [lightbox, setLightbox] = useState<{ urls: string[]; index: number } | null>(null);
  // Urun basina uretilmis gorsel sayisi (secicide "gorseli olmayanlar" suzgeci)
  const shotProducts = useShotProducts();
  const shotCounts = useMemo(() => new Map((shotProducts.data?.items ?? []).map((p) => [p.key, p.shotCount])), [shotProducts.data]);

  useEffect(() => { try { localStorage.setItem(SESSION_KEY, JSON.stringify(session)); } catch { /* dolu */ } }, [session]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [cats, conceptList, setRows, modelRes, settingsRes] = await Promise.all([
        apiClient.listSupplierCatalogs(vertical).catch(() => [] as SupplierCatalog[]),
        apiClient.listMockups("concept").catch(() => [] as CustomMockup[]),
        apiClient.listMockupCollections("concept").catch(() => []),
        fetch(`${getBackendUrl()}/api/analyses/design/models`, { headers: authHeaders() }).then((r) => r.json()).catch(() => ({})),
        fetch(`${getBackendUrl()}/api/settings`, { headers: authHeaders() }).then((r) => r.json()).catch(() => ({})),
      ]);
      if (cancelled) return;
      setCatalogs(cats ?? []);
      setConcepts(conceptList ?? []);
      // Konsept kutuphanesinden silinen sahneler kartlardan da duser ("Silinmis sahne" kalmasin)
      if (conceptList?.length) {
        const alive = new Set(conceptList.map((c) => c.id));
        setSession((st) => {
          let changed = false;
          const cards = Object.fromEntries(Object.entries(st.cards).map(([k, c]) => {
            if (c.conceptIds.every((id) => alive.has(id))) return [k, c];
            changed = true;
            const keep = (o?: Record<string, string>) => (o ? Object.fromEntries(Object.entries(o).filter(([id]) => alive.has(id))) : o);
            return [k, { ...c, conceptIds: c.conceptIds.filter((id) => alive.has(id)), conceptPrompts: keep(c.conceptPrompts) ?? {}, conceptRefs: keep(c.conceptRefs) }];
          }));
          return changed ? { ...st, cards } : st;
        });
      }
      setSets((setRows ?? []) as unknown as typeof sets);
      setModels((modelRes?.data ?? []) as typeof models);
      try { const raw = settingsRes?.data?.jewelry_shot_prefs; if (raw) setPrefs({ byType: {}, ...(JSON.parse(raw) as Partial<ShotPrefs>) }); } catch { /* */ }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [vertical]);

  // Kayitli mankenler: Studio'daki kayitli ayarlarin manken kismi ("Sahnedeki gibi" olanlar haric)
  const savedLooks: SavedLook[] = (prefs.presets ?? []).filter((p) => p.s.look && p.s.look.mode !== "keep").map((p) => ({ id: p.id, name: p.name, look: p.s.look! }));
  function saveLook(name: string, s: CardSettings) {
    const presets = prefs.presets ?? [];
    const existing = presets.find((x) => x.name === name);
    const entry = { id: existing?.id ?? `p_${Date.now()}`, name, s: { look: s.look, model: s.model, extraPrompt: s.extraPrompt } };
    const next: ShotPrefs = { ...prefs, presets: existing ? presets.map((x) => (x.id === entry.id ? entry : x)) : [...presets, entry] };
    setPrefs(next);
    void fetch(`${getBackendUrl()}/api/settings`, {
      method: "PUT", headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({ jewelry_shot_prefs: JSON.stringify(next) }),
    }).catch(() => setMsg("Manken kaydedilemedi"));
  }

  const rows = useMemo(() => {
    const out: Row[] = [];
    for (const c of catalogs) for (const cat of (c.categories ?? []) as ProductCategory[]) if (!cat.discontinued) out.push({ key: shotProductKey(c.id, cat.id), catalog: c, cat });
    return out;
  }, [catalogs]);
  const rowByKey = useMemo(() => new Map(rows.map((r) => [r.key, r])), [rows]);
  const conceptById = useMemo(() => new Map(concepts.map((c) => [c.id, c])), [concepts]);
  const selectedRows = session.selected.map((k) => rowByKey.get(k)).filter((x): x is Row => !!x);

  /** Yeni kartin varsayilanlari: turun son ayarlari + son sahne seti (tura uyanlar) */
  function defaultsFor(r: Row): CardSettings {
    const type = productJewelryType(r.cat);
    const p = type ? prefs.byType[type] : undefined;
    let lastModel = "", lastSet = "";
    try { lastModel = localStorage.getItem("erc_concept_model_v2") || ""; lastSet = localStorage.getItem("erc_concept_set") || ""; } catch { /* */ }
    const set = sets.find((s) => s.id === lastSet);
    const conceptIds = (set?.mockups ?? []).map((m) => m.id).filter((id) => conceptById.has(id));
    const perso = r.cat.personalization;
    return {
      conceptIds,
      setId: set?.id,
      conceptPrompts: {},
      // Turun eski model tercihi (LumiRoute / Image 2 denemeleri) kullanilmaz: varsayilan Sunburst,
      // yalniz Studio'da bilerek secilen model (erc_concept_model_v2) onun yerine gecer
      model: lastModel || (models.some((m) => m.id === "openai/gpt-image-2.5-sunburst") ? "openai/gpt-image-2.5-sunburst" : models[0]?.id) || "",
      look: p?.look ?? { mode: "keep" },
      extraPrompt: p?.extraPrompt ?? "",
      persoValues: perso?.enabled ? Array.from({ length: perso.slots }, (_, i) => perso.samples?.[i] ?? "") : [],
    };
  }
  const settingsOf = (r: Row): CardSettings => session.cards[r.key] ?? defaultsFor(r);
  const patchCard = (r: Row, p: Partial<CardSettings>) =>
    setSession((s) => ({ ...s, cards: { ...s.cards, [r.key]: { ...(s.cards[r.key] ?? defaultsFor(r)), ...p } } }));

  function addProducts(keys: string[]) {
    setSession((s) => {
      const cards = { ...s.cards };
      for (const k of keys) { const r = rowByKey.get(k); if (r && !cards[k]) cards[k] = defaultsFor(r); }
      return { ...s, selected: Array.from(new Set([...s.selected, ...keys])), cards };
    });
  }
  const removeProduct = (key: string) => setSession((s) => {
    const { [key]: _c, ...cards } = s.cards; const { [key]: _j, ...jobsMap } = s.jobs;
    return { selected: s.selected.filter((k) => k !== key), cards, jobs: jobsMap };
  });

  // ── Is ilerlemesi: aktif isler 5 sn'de bir yenilenir ──
  const jobIds = Object.values(session.jobs);
  const jobsRef = useRef(jobs); jobsRef.current = jobs;
  useEffect(() => {
    if (jobIds.length === 0) return;
    let cancelled = false;
    const poll = async () => {
      const active = jobIds.filter((id) => { const j = jobsRef.current[id]; return !j || j.status === "pending" || j.status === "processing"; });
      const res = await Promise.all(active.map((id) =>
        fetch(`${getBackendUrl()}/api/shot-jobs/${id}`, { headers: authHeaders() }).then((r) => (r.ok ? r.json() : null)).catch(() => null)));
      if (cancelled) return;
      setJobs((m) => { const n = { ...m }; res.forEach((b, i) => { if (b?.data) n[active[i]] = b.data as Job; }); return n; });
    };
    void poll();
    const t = setInterval(() => void poll(), 5000);
    return () => { cancelled = true; clearInterval(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobIds.join(",")]);

  const isRunning = (key: string) => { const id = session.jobs[key]; const j = id ? jobs[id] : undefined; return !!id && (!j || j.status === "pending" || j.status === "processing"); };
  // Toplu baslatma yalniz HIC baslatilmamis urunleri alir (bitenler tekrar uretilmesin;
  // bilincli yeniden uretim kartin "Yeniden uret"i ile, onayla)
  const ready = selectedRows.filter((r) => settingsOf(r).conceptIds.length > 0 && !session.jobs[r.key]);
  const totalShots = ready.reduce((n, r) => n + settingsOf(r).conceptIds.length, 0);

  async function startCard(r: Row, opts: { skipExisting?: boolean } = {}): Promise<boolean> {
    const s = settingsOf(r);
    if (s.conceptIds.length === 0) return false;
    // Secilen referans katalogda saklanir: kuyruk urunu oradan okur
    if (s.refUrl && s.refUrl !== (r.cat.referenceImageUrl ?? r.cat.source?.images?.[0])) {
      await fetch(`${getBackendUrl()}/api/supplier-catalogs/${r.catalog.id}/categories/${r.cat.id}/reference-image`, {
        method: "PATCH", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ url: s.refUrl }),
      }).catch(() => {});
    }
    const res = await fetch(`${getBackendUrl()}/api/shot-jobs`, {
      method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({
        catalogId: r.catalog.id,
        categoryIds: [r.cat.id],
        conceptIds: s.conceptIds,
        conceptPrompts: s.conceptPrompts,
        // Sahneye ozel foto yoksa kartta secili urun referansi: katalog kaydina
        // (PATCH) guvenilmez, her satir kendi fotografini tasir
        conceptProductRefs: Object.fromEntries(s.conceptIds.map((id) => [id, s.conceptRefs?.[id] || s.refUrl || ""]).filter(([, u]) => u)),
        extraPrompt: [personalizationClause(r.cat.personalization, s.persoValues), s.extraPrompt.trim()].filter(Boolean).join(" ") || undefined,
        model: s.model || undefined,
        modelLook: s.look.mode === "keep" ? undefined : s.look,
        ...(opts.skipExisting ? { skipExisting: true } : {}),
      }),
    });
    const b = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(b.error || "Kuyruğa alınamadı");
    const job = b.data as Job;
    setJobs((m) => ({ ...m, [job.id]: job }));
    setSession((x) => ({ ...x, jobs: { ...x.jobs, [r.key]: job.id } }));
    return true;
  }

  // Cift tiklama: state guncellenmeden ikinci tiklama ikinci isi baslatiyordu
  const startingRef = useRef(false);
  async function startAll() {
    if (startingRef.current) return;
    startingRef.current = true;
    setStarting(true); setMsg(null);
    let ok = 0; const errors: string[] = [];
    try {
      for (const r of ready) {
        // Sunucu da gorseli zaten olan urun+sahne ciftlerini atlar
        try { if (await startCard(r, { skipExisting: true })) ok++; } catch (e) { errors.push(`${r.cat.name}: ${e instanceof Error ? e.message : "hata"}`); }
      }
      setMsg(`${ok} ürün üretime alındı.${errors.length ? ` Atlanan / hata: ${errors.join("; ")}` : ""}`);
    } finally {
      startingRef.current = false;
      setStarting(false);
    }
  }

  /** Toplu islem: tum kartlara ayni ayari uygula */
  function applyToAll(p: Partial<CardSettings> | ((r: Row) => Partial<CardSettings>)) {
    setSession((s) => {
      const cards = { ...s.cards };
      for (const r of selectedRows) cards[r.key] = { ...(cards[r.key] ?? defaultsFor(r)), ...(typeof p === "function" ? p(r) : p) };
      return { ...s, cards };
    });
  }
  /** Setin sahneleri (silinmis sahneler atlanir). Tur suzgeci YOK: seti
   *  kullanici bilerek secti (Studio ile ayni) */
  const setConcepts4 = (setId: string): Partial<CardSettings> => {
    const hit = sets.find((x) => x.id === setId);
    try { localStorage.setItem("erc_concept_set", setId); } catch { /* */ }
    return { setId, conceptIds: (hit?.mockups ?? []).map((m) => m.id).filter((id) => conceptById.has(id)) };
  };

  const finishedKeys = selectedRows.filter((r) => { const id = session.jobs[r.key]; const j = id ? jobs[id] : undefined; return !!j && j.doneCount > 0 && !isRunning(r.key); }).map((r) => r.key);
  const anyRunning = selectedRows.some((r) => isRunning(r.key));
  // Anahtar acikken: hatasiz biten urun Liste Denetimi'ne eklenir ve buradan kalkar (29/10).
  // Sayfa acikken calisir; kapaliyken biten is, sayfa acilinca gonderilir. Hatalisi yeniden denensin diye kalir.
  const [autoReview, setAutoReview] = useState(() => { try { return localStorage.getItem(AUTO_REVIEW_KEY) === "1"; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem(AUTO_REVIEW_KEY, autoReview ? "1" : "0"); } catch { /* */ } }, [autoReview]);
  // Liste Denetimi'ndeki "AI ile otomatik yaz" ile ayni ayar (iki sayfada da gorunur)
  const [autoAi, setAutoAi] = useState(() => { try { return localStorage.getItem(AUTO_AI_KEY) === "1"; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem(AUTO_AI_KEY, autoAi ? "1" : "0"); } catch { /* */ } }, [autoAi]);
  const autoKeys = finishedKeys.filter((k) => !jobs[session.jobs[k]]?.failedCount);
  useEffect(() => {
    if (!autoReview || !autoKeys.length) return;
    queueForReview(autoKeys);
    const done = new Set(autoKeys);
    setSession((st) => ({
      selected: st.selected.filter((k) => !done.has(k)),
      cards: Object.fromEntries(Object.entries(st.cards).filter(([k]) => !done.has(k))),
      jobs: Object.fromEntries(Object.entries(st.jobs).filter(([k]) => !done.has(k))),
    }));
    setMsg(`${autoKeys.length} ürünün fotoğrafları hazır — Liste Denetimi'ne gönderildi.`);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoReview, autoKeys.join(",")]);
  const [stopping, setStopping] = useState(false);
  async function stopAll() {
    if (!confirm("Üretim durdurulsun mu? Bekleyen görseller iptal edilir, o an üretilen görsel tamamlanır.")) return;
    setStopping(true);
    try {
      const ids = selectedRows.filter((r) => isRunning(r.key)).map((r) => session.jobs[r.key]);
      await Promise.all(ids.map((id) => fetch(`${getBackendUrl()}/api/shot-jobs/${id}/cancel`, { method: "POST", headers: authHeaders() })));
      setJobs((m) => { const n = { ...m }; ids.forEach((id) => { if (n[id]) n[id] = { ...n[id], status: "cancelled" }; }); return n; });
    } finally { setStopping(false); }
  }

  if (loading) return <div className="flex items-center gap-2 p-8 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" /> Yükleniyor…</div>;

  return (
    <div className="mx-auto max-w-[1400px] p-6">
      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Sparkles className="h-6 w-6 text-fuchsia-600" />
        <div className="mr-auto">
          <h1 className="text-xl font-bold text-[#222]">Toplu Fotoğraf Üretimi</h1>
          <p className="text-[13px] text-[#595959]">Her ürünün sahneleri ve ayarları ayrıdır; hepsi tek tıkla kuyruğa girer (aynı anda 2 görsel üretilir).</p>
        </div>
        <Link to="/jewelry-studio" className="text-[13px] font-semibold text-[#222] underline">Tekli Studio</Link>
        <button onClick={() => setPickerOpen(true)} className="flex items-center gap-1.5 rounded-full border-2 border-[#222] bg-white px-4 py-1.5 text-sm font-bold text-[#222] hover:bg-[#f6f6f5]">
          <Plus className="h-4 w-4" /> Ürün ekle
        </button>
        <button onClick={() => void startAll()} disabled={starting || ready.length === 0}
          className="flex items-center gap-1.5 rounded-full bg-[#222] px-5 py-2 text-sm font-bold text-white hover:bg-black disabled:cursor-not-allowed disabled:opacity-40">
          {starting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
          Tümünü üretime başlat {ready.length > 0 && `(${ready.length} ürün · ${totalShots} görsel)`}
        </button>
        {anyRunning && (
          <button onClick={() => void stopAll()} disabled={stopping} title="Bekleyen görseller iptal edilir; o an üretilen görsel tamamlanır"
            className="flex items-center gap-1.5 rounded-full border-2 border-red-600 bg-white px-4 py-1.5 text-sm font-bold text-red-700 hover:bg-red-50 disabled:opacity-40">
            {stopping ? <Loader2 className="h-4 w-4 animate-spin" /> : <Square className="h-4 w-4" />} Durdur
          </button>
        )}
      </div>

      {selectedRows.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-3 text-[13px]">
          <span className="font-semibold text-[#222]">Tümüne uygula:</span>
          <select defaultValue="" onChange={(e) => { const v = e.target.value; if (v) applyToAll(setConcepts4(v)); e.target.value = ""; }}
            className="rounded-lg border border-gray-300 px-2 py-1">
            <option value="">Sahne seti…</option>
            {sets.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.mockups.length})</option>)}
          </select>
          <select defaultValue="" onChange={(e) => { const v = e.target.value; if (v) { applyToAll({ model: v }); rememberModel(v); } e.target.value = ""; }}
            className="rounded-lg border border-gray-300 px-2 py-1">
            <option value="">Model…</option>
            {models.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <select defaultValue="" onChange={(e) => {
            const v = e.target.value;
            const saved = savedLooks.find((x) => `p:${x.id}` === v);
            if (saved) applyToAll({ look: saved.look });
            else if (v) applyToAll({ look: { mode: v as LookMode } });
            e.target.value = "";
          }}
            className="rounded-lg border border-gray-300 px-2 py-1">
            <option value="">Manken…</option>
            <option value="keep">Sahnedeki gibi</option>
            <option value="auto">Farklı kişi (otomatik)</option>
            {savedLooks.length > 0 && (
              <optgroup label="Kayıtlı manken">
                {savedLooks.map((x) => <option key={x.id} value={`p:${x.id}`}>{x.name}</option>)}
              </optgroup>
            )}
          </select>
          <label className="ml-auto flex cursor-pointer items-center gap-1.5 font-semibold text-[#222]"
            title="Açıkken: fotoğrafları hatasız biten ürün Liste Denetimi'ne eklenir ve buradan kaldırılır (sayfa açıkken)">
            <input type="checkbox" checked={autoReview} onChange={(e) => setAutoReview(e.target.checked)} className="h-4 w-4 accent-emerald-600" />
            Bitince Liste Denetimi'ne gönder
          </label>
          <label className={`flex cursor-pointer items-center gap-1.5 font-semibold text-[#222] ${autoReview ? "" : "opacity-50"}`}
            title="Gönderilen ürünlerin başlık/açıklama/etiketi AI ile yazılır (atanan strateji; havuz yoksa önce Etsy'de aranır). Yazım Liste Denetimi sayfası açıkken yürür — başka sekmede açık tutabilirsin.">
            <input type="checkbox" checked={autoAi} onChange={(e) => setAutoAi(e.target.checked)} className="h-4 w-4 accent-fuchsia-600" />
            AI ile otomatik yaz
          </label>
          <span className="text-[#595959]">{selectedRows.length} ürün</span>
          {finishedKeys.length > 0 && (
            <button onClick={() => navigate(`/listing-review/jewelry?products=${encodeURIComponent(finishedKeys.join(","))}`)}
              className="flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 font-semibold text-white hover:bg-emerald-700">
              <ClipboardCheck className="h-4 w-4" /> Liste Denetimi'ne gönder ({finishedKeys.length})
            </button>
          )}
          {/* Fotografi uretilen (isi biten) urunler listeden kalkar; uretilmemisler kalir */}
          {finishedKeys.length > 0 && (
            <button onClick={() => setSession((st) => {
              const done = new Set(finishedKeys);
              const cards = Object.fromEntries(Object.entries(st.cards).filter(([k]) => !done.has(k)));
              const jobsMap = Object.fromEntries(Object.entries(st.jobs).filter(([k]) => !done.has(k)));
              return { selected: st.selected.filter((k) => !done.has(k)), cards, jobs: jobsMap };
            })} title="Fotoğrafı üretilen ürünleri listeden kaldır (görseller ürünün galerisinde kalır)"
              className="rounded-full border border-[#222] px-3 py-1 font-semibold text-[#222] hover:bg-[#f6f6f5]">
              Üretilenleri temizle ({finishedKeys.length})
            </button>
          )}
          <button onClick={() => { if (!anyRunning || confirm("Üretim sürüyor; liste temizlensin mi? (Kuyruk durmaz)")) setSession({ selected: [], cards: {}, jobs: {} }); }}
            className="text-[#595959] underline">Temizle</button>
        </div>
      )}
      {msg && <div className="mb-4 rounded-xl bg-[#fdf0d5] px-3 py-2 text-[13px] text-[#8a5a00]">{msg}</div>}

      {selectedRows.length === 0 ? (
        <div className="rounded-xl border border-dashed border-gray-300 p-12 text-center text-sm text-gray-500">
          Henüz ürün yok. <button onClick={() => setPickerOpen(true)} className="font-semibold text-[#222] underline">Ürün ekle</button> ile katalogdan seçin.
        </div>
      ) : (
        <div className="space-y-4">
          {selectedRows.map((r) => (
            <BulkCard key={r.key} row={r} s={settingsOf(r)} typeLabel={typeLabel(productJewelryType(r.cat))}
              concepts={concepts} conceptById={conceptById} sets={sets} models={models}
              savedLooks={savedLooks} onSaveLook={(name) => saveLook(name, settingsOf(r))}
              job={session.jobs[r.key] ? jobs[session.jobs[r.key]] : undefined} running={isRunning(r.key)}
              onPatch={(p) => patchCard(r, p)} onRemove={() => removeProduct(r.key)}
              onConceptAdded={(m) => setConcepts((prev) => [m, ...prev.filter((c) => c.id !== m.id)])}
              onApplySet={(id) => patchCard(r, setConcepts4(id))}
              onStart={async () => { try { await startCard(r); } catch (e) { setMsg(e instanceof Error ? e.message : "Hata"); } }}
              onRetry={async (jobId) => { await fetch(`${getBackendUrl()}/api/shot-jobs/${jobId}/retry-failed`, { method: "POST", headers: { ...authHeaders(), "Content-Type": "application/json" }, body: JSON.stringify({ model: settingsOf(r).model || undefined }) }); setJobs((m) => ({ ...m, [jobId]: { ...m[jobId], status: "processing" } })); }}
              onOpen={(urls, index) => setLightbox({ urls, index })} />
          ))}
        </div>
      )}

      {pickerOpen && (
        <ProductPicker rows={rows} chosen={new Set(session.selected)} typeLabel={typeLabel} shotCounts={shotCounts}
          onClose={() => setPickerOpen(false)} onAdd={(keys) => { addProducts(keys); setPickerOpen(false); }} />
      )}
      {lightbox && <ImageLightbox urls={lightbox.urls} index={lightbox.index} onClose={() => setLightbox(null)} onIndex={(i) => setLightbox((l) => (l ? { ...l, index: i } : l))} />}
    </div>
  );
}

/** Son secilen model: yeni kartlar ve Studio bununla acilir (Studio ile ortak anahtar) */
function rememberModel(m: string) {
  try { if (m) localStorage.setItem("erc_concept_model_v2", m); } catch { /* */ }
}

function BulkCard({ row: r, s, typeLabel, concepts, conceptById, sets, models, savedLooks, onSaveLook, job, running, onPatch, onRemove, onConceptAdded, onApplySet, onStart, onRetry, onOpen }: {
  row: Row; s: CardSettings; typeLabel: string;
  concepts: CustomMockup[]; conceptById: Map<string, CustomMockup>;
  sets: Array<{ id: string; name: string; mockups: Array<{ id: string }> }>;
  models: Array<{ id: string; name: string; price?: string }>;
  savedLooks: SavedLook[]; onSaveLook: (name: string) => void;
  job?: Job; running: boolean;
  onPatch: (p: Partial<CardSettings>) => void; onRemove: () => void; onApplySet: (id: string) => void;
  /** Ilham fotografindan konsept eklendi: ortak konsept listesine girer */
  onConceptAdded: (m: CustomMockup) => void;
  onStart: () => Promise<void>; onRetry: (jobId: string) => Promise<void>;
  onOpen: (urls: string[], index: number) => void;
}) {
  const [adding, setAdding] = useState(false);
  const [inspoOpen, setInspoOpen] = useState(false);
  const startLock = useRef(false);
  // Pencere acikken art arda eklenen fotograflar eski listeyle ezilmesin
  const latest = useRef({ ids: s.conceptIds, refs: s.conceptRefs ?? {} });
  latest.current = { ids: s.conceptIds, refs: s.conceptRefs ?? {} };
  const [busy, setBusy] = useState(false);
  const type = productJewelryType(r.cat);
  const images = r.cat.source?.images ?? [];
  const ref = s.refUrl ?? r.cat.referenceImageUrl ?? images[0];
  const perso = r.cat.personalization;
  // Tum konseptler secilebilir: once urunun turune uyanlar, sonra digerleri (baska tur / tursuz)
  const unpicked = concepts.filter((c) => !s.conceptIds.includes(c.id));
  const pickable = type ? unpicked.filter((c) => c.productTypeKey === type) : unpicked;
  const otherPickable = type ? unpicked.filter((c) => c.productTypeKey !== type) : [];
  const label = "mb-1 text-[12px] font-semibold text-[#222]";
  const results = (job?.items ?? []).filter((i) => i.imageUrl);

  return (
    <div className="rounded-2xl border border-[#e1e3df] bg-white p-4">
      <div className="mb-3 flex items-start gap-3">
        <img src={abs(ref)} alt="" className="h-16 w-16 flex-none cursor-zoom-in rounded-xl border border-[#e1e3df] object-cover" onClick={() => onOpen([abs(ref)], 0)} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-lg font-bold text-[#222]" title={r.cat.name}>{r.cat.name}</p>
          <p className="text-[13px] text-[#595959]">{typeLabel} · {r.cat.source?.sku ?? r.catalog.name}</p>
        </div>
        <JobBadge job={job} running={running} />
        <button onClick={onRemove} title="Listeden çıkar" className="rounded-full p-1.5 text-[#595959] hover:bg-[#f6f6f5]"><X className="h-4 w-4" /></button>
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_320px]">
        <div>
          {/* Sahneler: her biri ayri gorsel, sahneye ozel talimat */}
          <div className="mb-1 flex items-center gap-2">
            <p className={label}>Sahneler ({s.conceptIds.length})</p>
            <select value={s.setId && sets.some((x) => x.id === s.setId) ? s.setId : ""} onChange={(e) => e.target.value && onApplySet(e.target.value)} className="ml-auto rounded-lg border border-gray-300 px-2 py-0.5 text-[12px]">
              <option value="" disabled>Set uygula…</option>
              {sets.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
            </select>
            <button onClick={() => setInspoOpen(true)} title="Ürünün ilham analizlerindeki fotoğrafları sahne olarak ekle"
              className="rounded-full border border-[#222] px-2.5 py-0.5 text-[12px] font-semibold text-[#222] hover:bg-[#f6f6f5]">
              İlham
            </button>
            <button onClick={() => setAdding((v) => !v)} className="rounded-full border border-[#222] px-2.5 py-0.5 text-[12px] font-semibold text-[#222] hover:bg-[#f6f6f5]">
              {adding ? "Kapat" : "+ Sahne"}
            </button>
            {inspoOpen && (
              <InspirationConceptPicker cat={r.cat} productTypeKey={type} onClose={() => setInspoOpen(false)}
                library={concepts}
                picked={s.conceptIds.map((id) => ({ conceptId: id, productRef: s.conceptRefs?.[id] || ref || "" }))}
                onAdded={(m, productRef) => {
                  onConceptAdded(m);
                  // Pencere acikken art arda secimler eski listeyle ezilmesin
                  const ids = [...latest.current.ids.filter((x) => x !== m.id), m.id];
                  const refs = { ...latest.current.refs, [m.id]: productRef };
                  latest.current = { ids, refs };
                  onPatch({ conceptIds: ids, conceptRefs: refs });
                }}
                onRemoved={(id) => {
                  const ids = latest.current.ids.filter((x) => x !== id);
                  const refs = { ...latest.current.refs }; delete refs[id];
                  latest.current = { ids, refs };
                  onPatch({ conceptIds: ids, conceptRefs: refs });
                }} />
            )}
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-4">
            {s.conceptIds.map((id) => {
              const c = conceptById.get(id);
              return (
                <div key={id} className="rounded-xl border border-[#e1e3df] p-1.5">
                  <div className="relative">
                    <img src={abs(c?.thumbnailUrl ?? c?.imageUrl)} alt="" className="aspect-square w-full cursor-zoom-in rounded-lg object-cover" onClick={() => c && onOpen([abs(c.imageUrl)], 0)} />
                    {s.conceptRefs?.[id] && s.conceptRefs[id] !== ref && (
                      <img src={s.conceptRefs[id]} alt="" title="Bu sahnede kullanılacak ürün fotoğrafı"
                        className="absolute bottom-1 left-1 h-8 w-8 rounded-md border-2 border-white object-cover shadow" />
                    )}
                    <button onClick={() => onPatch({ conceptIds: s.conceptIds.filter((x) => x !== id) })} title="Kaldır"
                      className="absolute right-1 top-1 rounded-full bg-white/90 p-1 text-red-600 hover:bg-white"><Trash2 className="h-3 w-3" /></button>
                  </div>
                  <p className="mt-1 truncate text-[11px] text-[#595959]" title={c?.label}>{c?.label ?? "Silinmiş sahne"}</p>
                  <input value={s.conceptPrompts[id] ?? ""} onChange={(e) => onPatch({ conceptPrompts: { ...s.conceptPrompts, [id]: e.target.value } })}
                    placeholder="Bu sahneye özel talimat" className="mt-1 w-full rounded-md border border-gray-200 px-1.5 py-1 text-[11px]" />
                </div>
              );
            })}
            {s.conceptIds.length === 0 && <p className="col-span-full py-6 text-center text-[13px] text-[#595959]">Sahne seçilmedi — set uygulayın ya da sahne ekleyin.</p>}
          </div>
          {adding && (
            <div className="mt-2 rounded-xl bg-[#f6f6f5] p-2">
              <div className="max-h-80 space-y-2 overflow-y-auto">
                {[[type ? `${typeLabel} sahneleri` : "Sahneler", pickable] as const, ...(otherPickable.length ? [["Diğer konseptler (başka tür / türsüz)", otherPickable] as const] : [])]
                  .filter(([, list], i) => i > 0 || list.length > 0 || !otherPickable.length)
                  .map(([title, list]) => (
                  <div key={title}>
                    <p className="mb-1 text-[12px] font-semibold text-[#595959]">{title} ({list.length})</p>
                    <div className="grid grid-cols-4 gap-1.5 sm:grid-cols-6 xl:grid-cols-8">
                      {list.map((c) => (
                        <button key={c.id} onClick={() => onPatch({ conceptIds: [...s.conceptIds, c.id] })} title={c.label}
                          className="overflow-hidden rounded-lg border border-transparent hover:border-[#222]">
                          <img src={abs(c.thumbnailUrl ?? c.imageUrl)} alt="" loading="lazy" className="aspect-square w-full object-cover" />
                        </button>
                      ))}
                      {list.length === 0 && <p className="col-span-full py-3 text-center text-[12px] text-[#595959]">Eklenecek sahne yok</p>}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {results.length > 0 && (
            <div className="mt-3">
              <p className={label}>Üretilenler ({results.length})</p>
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 xl:grid-cols-8">
                {results.map((it, i) => (
                  <img key={it.id} src={abs(it.imageUrl)} alt="" className="aspect-square w-full cursor-zoom-in rounded-lg border border-[#e1e3df] object-cover"
                    onClick={() => onOpen(results.map((x) => abs(x.imageUrl)), i)} />
                ))}
              </div>
            </div>
          )}
          {(job?.items ?? []).some((i) => i.status === "error") && (
            <div className="mt-2 text-[12px] text-red-600">
              {(job!.items ?? []).filter((i) => i.status === "error").map((i) => (
                <p key={i.id}>{conceptById.get(i.conceptId ?? "")?.label ?? "Sahne"}: {i.error}</p>
              ))}
            </div>
          )}
        </div>

        {/* Karta ozel ayarlar */}
        <div className="space-y-3 rounded-xl bg-[#f6f6f5] p-3 text-[13px]">
          {images.length > 1 && (
            <div>
              <p className={label}>Ürün referansı</p>
              <div className="flex flex-wrap gap-1">
                {images.slice(0, 10).map((u) => (
                  <button key={u} onClick={() => onPatch({ refUrl: u })}
                    className={`h-11 w-11 overflow-hidden rounded-md border-2 ${u === ref ? "border-[#222]" : "border-transparent"}`}>
                    <img src={abs(u)} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
          <div>
            <p className={label}>Model</p>
            <select value={s.model} onChange={(e) => { onPatch({ model: e.target.value }); rememberModel(e.target.value); }} className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1">
              {models.map((m) => <option key={m.id} value={m.id}>{m.name}{m.price ? ` — ${m.price}` : ""}</option>)}
            </select>
          </div>
          <div>
            <p className={label}>Manken</p>
            <select value={(() => { const m = savedLooks.find((x) => sameLook(x.look, s.look)); return m ? `p:${m.id}` : s.look.mode; })()}
              onChange={(e) => {
                const v = e.target.value;
                const saved = savedLooks.find((x) => `p:${x.id}` === v);
                onPatch({ look: saved ? saved.look : { ...s.look, mode: v as LookMode } });
              }}
              className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1">
              <option value="keep">Sahnedeki gibi</option>
              <option value="auto">Farklı kişi (otomatik)</option>
              <option value="custom">Özel</option>
              {savedLooks.length > 0 && (
                <optgroup label="Kayıtlı manken">
                  {savedLooks.map((x) => <option key={x.id} value={`p:${x.id}`}>{x.name}</option>)}
                </optgroup>
              )}
            </select>
            {s.look.mode === "custom" && (
              <div className="mt-1.5 grid grid-cols-2 gap-1">
                {(Object.keys(LOOK_OPTIONS) as LookKey[]).map((k) => (
                  <select key={k} value={s.look[k] ?? ""} onChange={(e) => onPatch({ look: { ...s.look, [k]: e.target.value || undefined } })}
                    className="rounded-md border border-gray-300 bg-white px-1 py-0.5 text-[12px]">
                    <option value="">{LOOK_OPTIONS[k][0]}: serbest</option>
                    {LOOK_OPTIONS[k][1].map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                ))}
                {!savedLooks.some((x) => sameLook(x.look, s.look)) && (
                  <button type="button" onClick={() => { const n = prompt("Manken adı (aynı ad varsa güncellenir)"); if (n?.trim()) onSaveLook(n.trim()); }}
                    className="col-span-2 mt-0.5 rounded-md border border-dashed border-gray-400 px-2 py-0.5 text-[12px] font-semibold text-[#222] hover:bg-white">
                    + Bu mankeni kaydet
                  </button>
                )}
              </div>
            )}
          </div>
          {perso?.enabled && (
            <div>
              <p className={label}>Görselde yazacak {perso.slots > 1 ? "isimler" : "isim"}</p>
              <div className="flex flex-wrap gap-1">
                {Array.from({ length: perso.slots }, (_, i) => (
                  <input key={i} value={s.persoValues[i] ?? ""} placeholder={perso.samples?.[i] ?? `İsim ${i + 1}`}
                    onChange={(e) => { const v = [...s.persoValues]; v[i] = e.target.value; onPatch({ persoValues: v }); }}
                    className="w-28 rounded-md border border-gray-300 bg-white px-2 py-1" />
                ))}
              </div>
            </div>
          )}
          <div>
            <p className={label}>Ek talimat (tüm sahneler)</p>
            <textarea value={s.extraPrompt} onChange={(e) => onPatch({ extraPrompt: e.target.value })} rows={2}
              className="w-full rounded-lg border border-gray-300 bg-white px-2 py-1" placeholder="Örn. altın tonları sıcak olsun" />
          </div>
          <div className="flex gap-2">
            <button disabled={busy || running || s.conceptIds.length === 0} onClick={async () => {
              // Cift tiklama kilidi + bitmis urunde yeniden uretim onayi (ucretli, ayni sahneler)
              if (startLock.current) return;
              if (job && job.doneCount > 0 && !confirm(`Bu ürünün ${job.doneCount} görseli zaten üretildi. Aynı sahneler yeniden üretilecek (ücretli). Devam edilsin mi?`)) return;
              startLock.current = true; setBusy(true);
              try { await onStart(); } finally { setBusy(false); startLock.current = false; }
            }}
              className="flex flex-1 items-center justify-center gap-1.5 rounded-full bg-[#222] px-3 py-1.5 font-semibold text-white hover:bg-black disabled:opacity-40">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              {job && !running ? "Yeniden üret" : "Bu ürünü üret"} ({s.conceptIds.length})
            </button>
            {job && !running && job.failedCount > 0 && (
              <button onClick={() => void onRetry(job.id)} title="Hatalı olanları tekrar dene"
                className="flex items-center gap-1 rounded-full border border-[#222] px-3 py-1.5 font-semibold text-[#222]"><RotateCcw className="h-4 w-4" /> {job.failedCount}</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function JobBadge({ job, running }: { job?: Job; running: boolean }) {
  if (!job && !running) return null;
  if (running) {
    const done = (job?.doneCount ?? 0) + (job?.failedCount ?? 0);
    return (
      <span className="flex items-center gap-1.5 rounded-full bg-fuchsia-50 px-3 py-1 text-[12px] font-semibold text-fuchsia-700">
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Üretiliyor {done}/{job?.total ?? "…"}
      </span>
    );
  }
  if (!job) return null;
  return job.doneCount > 0
    ? <span className="flex items-center gap-1 rounded-full bg-emerald-50 px-3 py-1 text-[12px] font-semibold text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" /> {job.doneCount}/{job.total} hazır{job.failedCount ? ` · ${job.failedCount} hata` : ""}</span>
    : <span className="flex items-center gap-1 rounded-full bg-red-50 px-3 py-1 text-[12px] font-semibold text-red-700"><XCircle className="h-3.5 w-3.5" /> {job.status === "cancelled" ? "İptal" : "Başarısız"}</span>;
}

const SHOT_FILTER_KEY = "erc:jewelry-bulk-studio:shot-filter";
type ShotFilter = "all" | "none" | "has";

function ProductPicker({ rows, chosen, typeLabel, shotCounts, onClose, onAdd }: {
  rows: Row[]; chosen: Set<string>; typeLabel: (k: string | null) => string;
  /** Urun anahtari -> uretilmis gorsel sayisi */
  shotCounts: Map<string, number>;
  onClose: () => void; onAdd: (keys: string[]) => void;
}) {
  const [q, setQ] = useState("");
  const [type, setType] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  // Gorsel suzgeci: son secim hatirlanir
  const [shotFilter, setShotFilter] = useState<ShotFilter>(() => {
    try { return (localStorage.getItem(SHOT_FILTER_KEY) as ShotFilter) || "all"; } catch { return "all"; }
  });
  useEffect(() => { try { localStorage.setItem(SHOT_FILTER_KEY, shotFilter); } catch { /* */ } }, [shotFilter]);
  // Ilham listesi suzgeci (hatirlanir): ilham ilani eklenmis urunler sahne eslemesine hazir
  const [inspoFilter, setInspoFilter] = useState<"all" | "has" | "none">(() => {
    try { return (localStorage.getItem("erc_bulk_inspo_filter") as "all" | "has" | "none") || "all"; } catch { return "all"; }
  });
  useEffect(() => { try { localStorage.setItem("erc_bulk_inspo_filter", inspoFilter); } catch { /* */ } }, [inspoFilter]);
  const typeKeys = useMemo(() => Array.from(new Set(rows.map((r) => productJewelryType(r.cat) ?? ""))), [rows]);
  const shown = rows.filter((r) => {
    if (chosen.has(r.key)) return false;
    if (type && (productJewelryType(r.cat) ?? "") !== type) return false;
    const shots = shotCounts.get(r.key) ?? 0;
    if (shotFilter === "none" && shots > 0) return false;
    if (shotFilter === "has" && shots === 0) return false;
    const hasInspo = (r.cat.inspirations?.length ?? 0) > 0;
    if (inspoFilter === "has" && !hasInspo) return false;
    if (inspoFilter === "none" && hasInspo) return false;
    const n = q.trim().toLowerCase();
    return !n || r.cat.name.toLowerCase().includes(n) || (r.cat.source?.sku ?? "").toLowerCase().includes(n);
  });
  const toggle = (k: string) => setPicked((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="flex max-h-[90vh] w-full max-w-5xl flex-col rounded-2xl bg-white" onClick={(e) => e.stopPropagation()}>
        <div className="flex flex-wrap items-center gap-2 border-b p-3">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-2 h-4 w-4 text-gray-400" />
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ürün adı ya da SKU" className="w-full rounded-lg border border-gray-300 py-1.5 pl-8 pr-2 text-sm" />
          </div>
          <select value={type} onChange={(e) => setType(e.target.value)} className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
            <option value="">Tüm türler</option>
            {typeKeys.map((k) => <option key={k} value={k}>{typeLabel(k || null)}</option>)}
          </select>
          <select value={shotFilter} onChange={(e) => setShotFilter(e.target.value as ShotFilter)} className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
            <option value="all">Tüm ürünler</option>
            <option value="none">Görseli üretilmemiş</option>
            <option value="has">Görseli üretilmiş</option>
          </select>
          <select value={inspoFilter} onChange={(e) => setInspoFilter(e.target.value as "all" | "has" | "none")} aria-label="İlham listesi süzgeci" className="rounded-lg border border-gray-300 px-2 py-1.5 text-sm">
            <option value="all">İlham: tümü</option>
            <option value="has">İlham listesi olanlar ({rows.filter((r) => !chosen.has(r.key) && (r.cat.inspirations?.length ?? 0) > 0).length})</option>
            <option value="none">İlham listesi olmayanlar</option>
          </select>
          <button onClick={() => setPicked(new Set(shown.map((r) => r.key)))} className="text-sm text-[#222] underline">Görünenleri seç</button>
          <button onClick={onClose} className="rounded-full p-1.5 hover:bg-gray-100"><X className="h-4 w-4" /></button>
        </div>
        <div className="grid flex-1 grid-cols-3 gap-2 overflow-y-auto p-3 sm:grid-cols-5 lg:grid-cols-7">
          {shown.map((r) => {
            const on = picked.has(r.key);
            return (
              <button key={r.key} onClick={() => toggle(r.key)} className={`rounded-xl border-2 p-1 text-left ${on ? "border-[#222]" : "border-transparent hover:border-gray-300"}`}>
                <div className="relative">
                  <img src={abs(r.cat.referenceImageUrl ?? r.cat.source?.images?.[0])} alt="" className="aspect-square w-full rounded-lg object-cover" />
                  {(shotCounts.get(r.key) ?? 0) > 0 && (
                    <span className="absolute left-1 top-1 rounded-full bg-[#222]/80 px-1.5 text-[10px] font-semibold text-white" title="Üretilmiş görsel">
                      {shotCounts.get(r.key)} görsel
                    </span>
                  )}
                </div>
                <p className="mt-1 line-clamp-2 text-[11px] text-[#222]">{r.cat.name}</p>
              </button>
            );
          })}
          {shown.length === 0 && <p className="col-span-full py-8 text-center text-sm text-gray-500">Ürün yok</p>}
        </div>
        <div className="flex items-center justify-end gap-2 border-t p-3">
          <span className="mr-auto text-sm text-[#595959]">{picked.size} seçili</span>
          <button onClick={() => onAdd([...picked])} disabled={picked.size === 0}
            className="rounded-full bg-[#222] px-5 py-1.5 text-sm font-bold text-white disabled:opacity-40">Ekle</button>
        </div>
      </div>
    </div>
  );
}
